import { useState, useCallback } from 'react'
import { Screenshot, DEFAULT_SCREENSHOT_CONFIG } from '../types/screenshot.types'
import {
  captureFromClipboard,
  captureCurrentScreen,
  handleFileUpload,
  isClipboardSupported,
  isScreenCaptureSupported,
} from '../services/screenshot.service'

interface UseScreenshotsOptions {
  maxScreenshots?: number
  // Controlled mode
  screenshots?: Screenshot[]
  onChange?: (screenshots: Screenshot[]) => void
}

interface UseScreenshotsReturn {
  screenshots: Screenshot[]
  isCapturing: boolean
  error: string | null
  addFromClipboard: () => Promise<void>
  addFromCapture: () => Promise<void>
  addFromFile: (file: File) => Promise<void>
  removeScreenshot: (id: string) => void
  clearScreenshots: () => void
  clearError: () => void
  canPaste: boolean
  canCapture: boolean
}

export function useScreenshots(options: UseScreenshotsOptions = {}): UseScreenshotsReturn {
  const {
    maxScreenshots = DEFAULT_SCREENSHOT_CONFIG.maxScreenshots,
    screenshots: controlledScreenshots,
    onChange,
  } = options

  // Internal state for uncontrolled mode
  const [internalScreenshots, setInternalScreenshots] = useState<Screenshot[]>([])
  const [isCapturing, setIsCapturing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Use controlled or internal state
  const isControlled = controlledScreenshots !== undefined && onChange !== undefined
  const screenshots = isControlled ? controlledScreenshots : internalScreenshots

  const setScreenshots = useCallback(
    (updater: Screenshot[] | ((prev: Screenshot[]) => Screenshot[])) => {
      if (isControlled && onChange) {
        const newValue = typeof updater === 'function' ? updater(controlledScreenshots) : updater
        onChange(newValue)
      } else {
        setInternalScreenshots(updater as Screenshot[] | ((prev: Screenshot[]) => Screenshot[]))
      }
    },
    [isControlled, onChange, controlledScreenshots]
  )

  const canAdd = screenshots.length < maxScreenshots

  const addFromClipboard = useCallback(async () => {
    if (!canAdd) {
      setError(`Maximum ${maxScreenshots} screenshots allowed`)
      return
    }

    setIsCapturing(true)
    setError(null)

    try {
      const screenshot = await captureFromClipboard()
      if (screenshot) {
        setScreenshots((prev: Screenshot[]) => [...prev, screenshot])
      } else {
        setError('No image found in clipboard')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to paste from clipboard')
    } finally {
      setIsCapturing(false)
    }
  }, [canAdd, maxScreenshots, setScreenshots])

  const addFromCapture = useCallback(async () => {
    if (!canAdd) {
      setError(`Maximum ${maxScreenshots} screenshots allowed`)
      return
    }

    setIsCapturing(true)
    setError(null)

    try {
      const screenshot = await captureCurrentScreen()
      setScreenshots((prev: Screenshot[]) => [...prev, screenshot])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to capture screen')
    } finally {
      setIsCapturing(false)
    }
  }, [canAdd, maxScreenshots, setScreenshots])

  const addFromFile = useCallback(
    async (file: File) => {
      if (!canAdd) {
        setError(`Maximum ${maxScreenshots} screenshots allowed`)
        return
      }

      setIsCapturing(true)
      setError(null)

      try {
        const screenshot = await handleFileUpload(file)
        setScreenshots((prev: Screenshot[]) => [...prev, screenshot])
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to upload file')
      } finally {
        setIsCapturing(false)
      }
    },
    [canAdd, maxScreenshots, setScreenshots]
  )

  const removeScreenshot = useCallback(
    (id: string) => {
      setScreenshots((prev: Screenshot[]) => prev.filter((s: Screenshot) => s.id !== id))
    },
    [setScreenshots]
  )

  const clearScreenshots = useCallback(() => {
    setScreenshots([])
  }, [setScreenshots])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return {
    screenshots,
    isCapturing,
    error,
    addFromClipboard,
    addFromCapture,
    addFromFile,
    removeScreenshot,
    clearScreenshots,
    clearError,
    canPaste: isClipboardSupported(),
    canCapture: isScreenCaptureSupported(),
  }
}
